import * as L from 'leaflet';
import { DEFAULT_RADIUS_MILES, milesToMeters } from '@/utils/shipmentRadius';
import { getTooltipContent } from './shape';

export type PublicShipmentStop = {
  sequence?: number;
  type?: string;
  name?: string | null;
  geofence_id?: number | null;
  center?: { lat: number; lng: number } | null;
  radius?: number | null;
  geojson?: Record<string, unknown> | null;
};

export type StopMapGeometry =
  | { kind: 'geojson'; geojson: Record<string, unknown> }
  | { kind: 'circle'; lat: number; lng: number; radius: number };

const STOP_SHAPE_STYLE = {
  color: '#58CE50',
  weight: 2,
  opacity: 0.7,
  fillColor: '#fff',
  fillOpacity: 0.7,
};

export function sortedStops(
  stops: PublicShipmentStop[] | undefined | null,
): PublicShipmentStop[] {
  if (!Array.isArray(stops) || !stops.length) {
    return [];
  }
  return [...stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

export function stopMapGeometry(
  stop: PublicShipmentStop | undefined | null,
): StopMapGeometry | null {
  if (!stop) {
    return null;
  }
  if (stop.geojson && typeof stop.geojson === 'object') {
    return { kind: 'geojson', geojson: stop.geojson };
  }
  const lat = stop.center?.lat;
  const lng = stop.center?.lng;
  if (lat == null || lng == null) {
    return null;
  }
  const radius =
    stop.radius != null && Number(stop.radius) > 0
      ? Number(stop.radius)
      : milesToMeters(DEFAULT_RADIUS_MILES);
  return { kind: 'circle', lat: Number(lat), lng: Number(lng), radius };
}

export function stopLatLng(
  stop: PublicShipmentStop | undefined | null,
): { lat: number; lng: number } | null {
  if (!stop) {
    return null;
  }
  if (stop.center?.lat != null && stop.center?.lng != null) {
    return { lat: Number(stop.center.lat), lng: Number(stop.center.lng) };
  }
  const coords = firstGeoJsonCoordinate(stop.geojson);
  if (!coords) {
    return null;
  }
  return { lat: coords[1], lng: coords[0] };
}

export function createStopShape(
  stop: PublicShipmentStop | undefined | null,
): L.Layer | null {
  const geometry = stopMapGeometry(stop);
  if (!geometry) {
    return null;
  }
  if (geometry.kind === 'geojson') {
    return L.geoJSON(geometry.geojson as GeoJSON.GeoJsonObject, {
      style: STOP_SHAPE_STYLE,
    });
  }
  return L.circle([geometry.lat, geometry.lng], {
    radius: geometry.radius,
    latlng: { lat: geometry.lat, lng: geometry.lng },
    ...STOP_SHAPE_STYLE,
  });
}

export function createStopTooltip(
  stop: PublicShipmentStop,
  direction: string,
): L.Tooltip | null {
  const ll = stopLatLng(stop);
  if (!ll) {
    return null;
  }
  const name = stop.name?.trim() || '-';
  return L.tooltip({
    permanent: false,
    sticky: true,
    direction: 'top',
    opacity: 0.9,
  })
    .setLatLng(L.latLng([ll.lat, ll.lng]))
    .setContent(getTooltipContent(name, direction));
}

function firstGeoJsonCoordinate(
  geojson: Record<string, unknown> | null | undefined,
): [number, number] | null {
  if (!geojson || typeof geojson !== 'object') {
    return null;
  }
  const geometry =
    (geojson.geometry as Record<string, unknown> | undefined) ?? geojson;
  const coordinates = geometry.coordinates as unknown;
  const first = unwrapFirstLngLat(coordinates);
  if (!first) {
    return null;
  }
  return first;
}

function unwrapFirstLngLat(coordinates: unknown): [number, number] | null {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return null;
  }
  if (
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  ) {
    return [coordinates[0], coordinates[1]];
  }
  return unwrapFirstLngLat(coordinates[0]);
}
